import { jwtDecode } from 'jwt-decode'
import { User, UserManager } from 'oidc-client-ts'
import { createApiClient } from '../api/createApiClient'
import { IAuthenticationManager } from './IAuthenticationManager'
import { IIdToken } from './IIdToken'
import { ISession } from './ISession'
import { IUser } from './IUser'

class OidcUser implements IUser {
  constructor(private user: User) {}

  get attributes(): Record<string, any> {
    return this.user.profile
  }
  get username(): string {
    return this.user.profile.email || this.user.profile.sub
  }
  get displayName(): string {
    return this.user.profile.name || this.user.profile.email || this.user.profile.sub
  }

  get underlyingUserObject(): User {
    return this.user
  }
}

class OidcSession implements ISession {
  constructor(private idToken: IIdToken) {}

  getIdToken(): IIdToken {
    return this.idToken
  }
}

class OidcIdToken implements IIdToken {
  public payload: { [key: string]: any }

  constructor(private jwt: string) {
    this.payload = jwtDecode(jwt)
  }

  getJwtToken(): string {
    return this.jwt
  }

  getExpiration(): number {
    return this.payload['exp']
  }

  getIssuedAt(): number {
    return this.payload['iat']
  }
}

export class KeycloakAuthenticationManager implements IAuthenticationManager {
  private readonly userManager: UserManager

  constructor(private config: { authority: string; client_id: string; redirect_uri: string; torqCloudBaseUrl: string }) {
    this.userManager = new UserManager({
      authority: config.authority,
      client_id: config.client_id,
      scope: 'openid profile email offline_access',
      redirect_uri: config.redirect_uri,
      automaticSilentRenew: true,
    })
  }

  async currentAuthenticatedUser(): Promise<IUser | undefined> {
    const user = await this.userManager.getUser()
    if (user?.expires_at && new Date(user.expires_at * 1000) > new Date()) {
      return new OidcUser(user)
    }
  }

  async currentSession(): Promise<ISession | undefined> {
    const user = await this.userManager.getUser()
    if (user?.id_token && user?.expires_at && new Date(user.expires_at * 1000) > new Date()) {
      return new OidcSession(new OidcIdToken(user.id_token))
    }
  }

  async signIn(email: string, password: string): Promise<IUser> {
    const user = await this.userManager.signinResourceOwnerCredentials({
      username: email,
      password: password,
    })

    return new OidcUser(user)
  }

  signOut(): Promise<void> {
    return this.userManager.signoutRedirect()
  }

  async changePassword(user: IUser, oldPassword: string, newPassword: string): Promise<void> {
    const torqCloudApiClient = createApiClient(this, this.config.torqCloudBaseUrl, { 'Content-type': 'application/json' })
    await torqCloudApiClient.post(`/api/v1/users/change_password/`, { oldPassword, newPassword })
  }

  accountLinks = {
    parent: this as KeycloakAuthenticationManager,
    get signUpLink() {
      return `${this.parent.config.authority}/protocol/openid-connect/registrations?client_id=${this.parent.config.client_id}&response_type=code&scope=openid%20email&redirect_uri=${window.location.href}&kc_locale=en`
    },

    get forgotPasswordLink() {
      return `${this.parent.config.authority}/login-actions/reset-credentials?client_id=${this.parent.config.client_id}`
    },
  }
}
